import { useMemo, useState } from 'react';
import { Search, Library, Sparkles } from 'lucide-react';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { DiscoveryCard } from './discovery-card';
import { LITERARY_TERM_DEFINITIONS } from '@/lib/constants/literary-terms';
import {
  formatChiasmPattern,
  formatParallelismPattern,
  formatInclusioPattern,
  formatTricolonPattern,
  formatMerismPattern
} from './literary-context-utils';

interface MicroStructureCardProps {
  structure: {
    type: string;
    pattern: string;
    verses: string[];
    confidence?: Confidence;
    sources?: Source[];
  };
  onStructureClick?: (type: string) => void;
  isExpanded?: boolean;
  onToggleExpansion?: () => void;
}

/**
 * Shared component for displaying a single micro-structure (chiasm, parallelism, etc.)
 * Used in both the literary context page and the capability modal
 */
export function MicroStructureCard({
  structure,
  onStructureClick,
  isExpanded: controlledIsExpanded,
  onToggleExpansion: controlledOnToggle
}: MicroStructureCardProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = controlledIsExpanded ?? internalExpanded;
  const onToggle = controlledOnToggle ?? (() => setInternalExpanded(!internalExpanded));

  // Get definition if available
  const definition = LITERARY_TERM_DEFINITIONS[structure.type] ||
    LITERARY_TERM_DEFINITIONS[structure.type.split(' (')[0]] ||
    null;

  // Format the structure based on its type
  const formattedStructure = useMemo(() => {
    const typeLower = structure.type.toLowerCase();
    const isChiasm = typeLower === 'chiasm';
    const isParallelism = typeLower.includes('parallelism') && typeLower !== 'chiasm';
    const isInclusio = typeLower === 'inclusio';
    const isTricolon = typeLower === 'tricolon';
    const isMerism = typeLower === 'merism';

    return {
      isChiasm,
      isParallelism,
      isInclusio,
      isTricolon,
      isMerism,
      chiasmElements: isChiasm ? formatChiasmPattern(structure.pattern) : [],
      parallelismElements: isParallelism ? formatParallelismPattern(structure.pattern, structure.type) : [],
      inclusioElements: isInclusio ? formatInclusioPattern(structure.pattern) : null,
      tricolonElements: isTricolon ? formatTricolonPattern(structure.pattern) : [],
      merismElements: isMerism ? formatMerismPattern(structure.pattern) : []
    };
  }, [structure.type, structure.pattern]);

  return (
    <DiscoveryCard
      isExpanded={isExpanded}
      onToggle={onToggle}
      icon={Library}
      header={
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div
                className={`flex items-center gap-1.5 ${onStructureClick ? 'cursor-pointer hover:text-primary transition-colors group' : ''}`}
                onClick={(e) => {
                  if (onStructureClick) {
                    e.stopPropagation();
                    onStructureClick(structure.type);
                  }
                }}
              >
                <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                  {structure.type}
                </h4>
                {onStructureClick && (
                  <Search className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                )}
              </div>
              <span className="text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                {structure.verses.map(v => `${v}`).join(', ')}
              </span>
            </div>

            {definition && (
              <p className="text-xs text-muted-foreground/80 leading-relaxed font-medium max-w-[90%]">
                {definition}
              </p>
            )}

            {!isExpanded && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-1 italic font-light">
                  {structure.pattern}
                </p>
                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                  <Sparkles className="h-2.5 w-2.5" />
                  <span>Discover Significance</span>
                </div>
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6 mt-4 relative z-10">
        <div className="flex items-center gap-2 px-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Pattern Analysis</span>
          {structure.confidence && typeof structure.confidence === 'string' && (
            <ConfidenceBadge
              confidence={structure.confidence}
              context="general"
            />
          )}
        </div>

        {/* Render based on structure type */}
        {formattedStructure.isChiasm ? (
          <div className="text-xs space-y-1.5 ml-1 border-l-2 border-primary/10 pl-4 py-1">
            {formattedStructure.chiasmElements.map((element, idx) => (
              <div
                key={idx}
                style={{ paddingLeft: `${element.level * 1.5}rem` }}
                className="text-muted-foreground"
              >
                <span className="font-medium text-foreground">{element.label}</span>
                {element.content && <span> {element.content}</span>}
              </div>
            ))}
          </div>
        ) : formattedStructure.isParallelism ? (
          <div className="text-xs space-y-3 ml-1 border-l-2 border-primary/10 pl-4 py-1">
            {formattedStructure.parallelismElements.map((element, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="font-medium text-foreground min-w-[1.5rem]">{element.label}:</span>
                <span className="text-muted-foreground flex-1">{element.content}</span>
              </div>
            ))}
          </div>
        ) : formattedStructure.isInclusio && formattedStructure.inclusioElements ? (
          <div className="text-xs ml-1 border-l-2 border-primary/10 pl-4 py-1 space-y-3">
            <div className="flex items-start gap-2">
              <span className="font-medium text-foreground">Opening:</span>
              <span className="text-muted-foreground">{formattedStructure.inclusioElements.opening}</span>
            </div>
            {formattedStructure.inclusioElements.middle && (
              <div className="text-muted-foreground italic pl-4 text-[0.7rem]">
                ... {formattedStructure.inclusioElements.middle} ...
              </div>
            )}
            <div className="flex items-start gap-2">
              <span className="font-medium text-foreground">Closing:</span>
              <span className="text-muted-foreground">{formattedStructure.inclusioElements.closing}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm font-light text-foreground/80 leading-relaxed px-1">
            {structure.pattern}
          </div>
        )}

        {/* Sources */}
        {structure.sources && structure.sources.length > 0 && (
          <div className="pt-4 border-t border-border/10">
            <SourceLinks sources={structure.sources} />
          </div>
        )}
      </div>
    </DiscoveryCard>
  );
}

interface MicroStructuresListProps {
  structures: MicroStructureCardProps['structure'][];
  onStructureClick?: (type: string) => void;
}

/**
 * List wrapper for multiple micro-structures
 */
export function MicroStructuresList({ structures, onStructureClick }: MicroStructuresListProps) {
  if (!structures || structures.length === 0) return null;

  return (
    <div className="space-y-3">
      {structures.map((structure, index) => (
        <MicroStructureCard
          key={index}
          structure={structure}
          onStructureClick={onStructureClick}
        />
      ))}
    </div>
  );
}
